import React from 'react';

/** macOS-style shimmer bone — inline styles so it always paints */
export const Skeleton = ({ className = '', style = {} }) => (
  <div
    className={`skeleton-bone ${className}`}
    style={style}
    aria-hidden="true"
  />
);

/** Upcoming Closures table skeleton (7 columns) */
export const ClosureTableSkeleton = ({ rows = 6 }) => (
  <>
    {Array.from({ length: rows }).map((_, index) => (
      <tr key={`closure-skel-${index}`} className="border-b border-[#f0f0f2]">
        <td className="px-6 py-5">
          <Skeleton className="h-3.5 w-36 rounded-md" />
        </td>
        <td className="px-6 py-5">
          <Skeleton className="h-7 w-32 rounded-full mx-auto" />
        </td>
        <td className="px-6 py-5">
          <Skeleton className="h-7 w-28 rounded-full mx-auto" />
        </td>
        <td className="px-6 py-5">
          <Skeleton className="h-7 w-16 rounded-full mx-auto" />
        </td>
        <td className="px-6 py-5">
          <Skeleton className="h-7 w-24 rounded-full mx-auto" />
        </td>
        <td className="px-6 py-5">
          <Skeleton className="h-7 w-24 rounded-full mx-auto" />
        </td>
        <td className="px-6 py-5 whitespace-nowrap">
          <Skeleton className="h-4 w-24 rounded-md" />
        </td>
      </tr>
    ))}
  </>
);

/** Investor deposit details table skeleton */
export const InvestorDetailsTableSkeleton = ({ rows = 6 }) => (
  <>
    {Array.from({ length: rows }).map((_, index) => (
      <tr
        key={`details-skel-${index}`}
        className="bg-white border-8 border-[#F6F6F6] rounded-md"
      >
        {Array.from({ length: 9 }).map((__, col) => (
          <td
            key={`details-skel-${index}-${col}`}
            className="align-middle px-6 py-4 whitespace-nowrap text-center"
          >
            <Skeleton
              className={`h-7 rounded-full mx-auto ${
                col === 0 ? 'w-28' : col === 1 ? 'w-24' : col === 8 ? 'w-16' : 'w-20'
              }`}
            />
          </td>
        ))}
      </tr>
    ))}
  </>
);

/** Toolbar / action bar skeleton for details page */
export const DetailsToolbarSkeleton = () => (
  <div className="flex gap-3 items-center">
    <Skeleton className="hidden md:block h-10 w-40 rounded-xl" />
    <Skeleton className="hidden md:block h-10 w-28 rounded-xl" />
    <Skeleton className="h-10 w-24 rounded-xl" />
    <Skeleton className="h-10 w-28 rounded-xl" />
  </div>
);

const COMPLETED_HEADERS_BASE = [
  'Investor Name',
  'CNIC',
  'Plan Name',
  'Deposit',
  'Profit',
  'Loss',
  'Deduction',
  'Net Amount',
  'Years',
  'Payment Status',
  'Deposit Date'
];

/** Completed Plan table skeleton (manager / super admin) */
export const CompletedPlanTableSkeleton = ({ rows = 6, showActions = false }) => {
  const headers = showActions
    ? [...COMPLETED_HEADERS_BASE, 'Closing Date', 'Actions']
    : COMPLETED_HEADERS_BASE;

  const cells = showActions
    ? [
        { className: 'h-4 w-36 rounded-md' },
        { className: 'h-7 w-28 rounded-full' },
        { className: 'h-7 w-24 rounded-full' },
        { className: 'h-4 w-24 rounded-md' },
        { className: 'h-4 w-20 rounded-md' },
        { className: 'h-4 w-20 rounded-md' },
        { className: 'h-4 w-20 rounded-md' },
        { className: 'h-9 w-28 rounded-lg' },
        { className: 'h-7 w-16 rounded-full' },
        { className: 'h-7 w-24 rounded-full' },
        { className: 'h-4 w-24 rounded-md' },
        { className: 'h-4 w-24 rounded-md' },
        { actions: true }
      ]
    : [
        { className: 'h-4 w-36 rounded-md' },
        { className: 'h-7 w-28 rounded-full' },
        { className: 'h-7 w-24 rounded-full' },
        { className: 'h-4 w-24 rounded-md' },
        { className: 'h-4 w-20 rounded-md' },
        { className: 'h-4 w-20 rounded-md' },
        { className: 'h-4 w-20 rounded-md' },
        { className: 'h-9 w-28 rounded-lg' },
        { className: 'h-7 w-16 rounded-full' },
        { className: 'h-7 w-24 rounded-full' },
        { className: 'h-4 w-24 rounded-md' }
      ];

  return (
    <div className="overflow-x-auto rounded-xl shadow-lg border border-gray-200 bg-white">
      <div className={showActions ? 'min-w-[1400px]' : 'min-w-[1200px]'}>
        <table className="w-full bg-white">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              {headers.map((label) => (
                <th
                  key={label}
                  className={`px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap ${
                    label === 'Actions' ? 'text-center' : ''
                  }`}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {Array.from({ length: rows }).map((_, index) => (
              <tr key={`completed-skel-${index}`} className="bg-white">
                {cells.map((cell, col) => (
                  <td key={`completed-skel-${index}-${col}`} className="px-6 py-5 whitespace-nowrap">
                    {cell.actions ? (
                      <div className="flex justify-center items-center gap-2">
                        <Skeleton className="h-9 w-20 rounded-md" />
                        <Skeleton className="h-9 w-20 rounded-md" />
                      </div>
                    ) : (
                      <Skeleton className={cell.className} />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/** Investors list table skeleton */
export const InvestorsTableSkeleton = ({ rows = 8, showActions = false }) => (
  <>
    {Array.from({ length: rows }).map((_, index) => (
      <tr
        key={`investor-skel-${index}`}
        className="bg-white border-t-8 border-b-8 border-[#F6F6F6] rounded-md"
      >
        <td className="px-6 py-4">
          <Skeleton className="h-4 w-36 rounded-md" />
        </td>
        <td className="px-6 py-4">
          <Skeleton className="h-7 w-28 rounded-full mx-auto" />
        </td>
        <td className="px-6 py-4">
          <Skeleton className="h-7 w-28 rounded-full mx-auto" />
        </td>
        <td className="px-6 py-4">
          <Skeleton className="h-7 w-16 rounded-full mx-auto" />
        </td>
        <td className="px-6 py-4">
          <Skeleton className="h-7 w-20 rounded-full mx-auto" />
        </td>
        {showActions ? (
          <td className="px-6 py-4">
            <div className="flex justify-center items-center gap-2">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-8 w-20 rounded-md" />
            </div>
          </td>
        ) : (
          <td className="px-6 py-4">
            <Skeleton className="h-9 w-20 rounded-md mx-auto" />
          </td>
        )}
      </tr>
    ))}
  </>
);

/** Header / filter strip skeleton for Completed Plan */
export const CompletedPlanHeaderSkeleton = ({ showFilters = true }) => (
  <div className="mb-6 space-y-4">
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
      <div className="space-y-2">
        <Skeleton className="hidden sm:block h-8 w-56 rounded-lg" />
        <Skeleton className="h-4 w-48 rounded-md" />
      </div>
      <Skeleton className="h-10 w-36 rounded-lg" />
    </div>
    {showFilters && (
      <div className="flex flex-wrap gap-2">
        <Skeleton className="h-8 w-16 rounded-full" />
        <Skeleton className="h-8 w-24 rounded-full" />
        <Skeleton className="h-8 w-20 rounded-full" />
        <Skeleton className="h-8 w-24 rounded-full" />
        <Skeleton className="h-8 w-28 rounded-full" />
        <Skeleton className="h-8 w-28 rounded-full" />
      </div>
    )}
  </div>
);
